/**
 * Responsibility: advance a train along a surveyed route and say where the rider's eye is.
 *
 * ## The train has memory and the world does not
 *
 * Terrain is a pure function of `(seed, x, z)` and holds no state. The train is the opposite: its whole
 * situation is one number, how far along the line it has travelled. That is not a violation of anything —
 * it is player state, exactly like the aircraft's position — and keeping the distinction sharp is what
 * lets an authored route live in a stateless world.
 *
 * Consequently there is nothing here to integrate but distance. Position, height, heading and curvature all
 * come from asking the route about a distance, so the train cannot drift off the rails however long it runs.
 *
 * ## What driving a train amounts to
 *
 * Speed, and where you are looking. That is the whole control surface, and it is deliberately less than the
 * aircraft has: the reason to be on rails rather than in the air is that you are not steering.
 *
 * **Stopping is allowed, which the flight model forbids.** `FLIGHT.SPEED_TRIM_MIN` is held well above zero
 * because "hovering would turn a flight into a photograph". Standing still to look at something is the
 * opposite case, and being unable to would be the omission rather than the discipline.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { damp } from '../core/damp.js';
import { RAILWAY, degToRad } from '../config.js';

/** Neutral controls, so a caller with nothing held need not construct an object. */
const NEUTRAL = Object.freeze({ look: 0 });

/** Clamp a value into a range. */
function clamp(value, low, high) {
  return value < low ? low : value > high ? high : value;
}

/**
 * Build the train model.
 *
 * @param {object} [tuning] - Overrides for the `RAILWAY` config block.
 * @returns {object}
 */
export function createTrainModel(tuning = RAILWAY) {
  const merged = tuning === RAILWAY ? RAILWAY : { ...RAILWAY, ...tuning };

  for (const key of ['MAX_SPEED', 'SPEED_STEPS', 'SPEED_TIME_CONSTANT', 'LOOK_LIMIT', 'MAX_CANT']) {
    if (!Number.isFinite(merged[key])) {
      throw new Error(`createTrainModel: ${key} must be a finite number`);
    }
  }
  if (!Number.isInteger(merged.SPEED_STEPS) || merged.SPEED_STEPS < 1) {
    throw new Error('createTrainModel: SPEED_STEPS must be an integer of at least 1');
  }

  const lookLimit = degToRad(merged.LOOK_LIMIT);
  const maxCant = degToRad(merged.MAX_CANT);

  /** Speed a step asks for. Step 0 is a full stand. */
  const speedForStep = (step) => (clamp(step, 0, merged.SPEED_STEPS) / merged.SPEED_STEPS) * merged.MAX_SPEED;

  return {
    /**
     * A train at rest, or wherever a save left it.
     *
     * @param {object} [placement]
     * @param {number} [placement.distance] - Along the line, in world units.
     * @param {number} [placement.step] - Speed step. Defaults to the middle, so a new world moves.
     * @returns {object}
     */
    createInitialState({ distance = 0, step } = {}) {
      /*
        Half throttle rather than stopped. A world that opens motionless looks broken, and the whole
        experience is a journey — but a *loaded* world keeps whatever it was doing, including stopped.
      */
      const initialStep = Number.isFinite(step) ? clamp(Math.round(step), 0, merged.SPEED_STEPS) : Math.ceil(merged.SPEED_STEPS / 2);

      return {
        distance: Number.isFinite(distance) ? distance : 0,
        step: initialStep,

        /*
          Already at the target rather than easing up from zero. Easing is for a change the player made; the
          first frame of a world is not a change.
        */
        speed: speedForStep(initialStep),
        look: 0,
      };
    },

    /**
     * Advance one fixed step.
     *
     * @param {object} state
     * @param {{look: number}} controls - `look` is -1..1, held.
     * @param {number} dt - Seconds.
     * @returns {object} A new state; the input is not modified.
     */
    step(state, controls = NEUTRAL, dt) {
      const target = speedForStep(state.step);
      const speed = damp(state.speed, target, dt, merged.SPEED_TIME_CONSTANT);

      /*
        Look eases toward where it is being held and back to centre when released, which is what makes it feel
        like turning your head rather than snapping a camera. Same damping as everything else in this project.
      */
      const look = damp(state.look, clamp(controls.look ?? 0, -1, 1) * lookLimit, dt, merged.LOOK_TIME_CONSTANT);

      return {
        distance: state.distance + speed * dt,
        step: state.step,
        speed,
        look,
      };
    },

    /**
     * Change the speed step, clamped.
     *
     * @param {object} state
     * @param {number} direction - +1 or -1.
     * @returns {object}
     */
    withStep(state, direction) {
      return { ...state, step: clamp(state.step + Math.sign(direction), 0, merged.SPEED_STEPS) };
    },

    /** Set the speed step outright, for the menu and for a loaded save. */
    withStepAt(state, step) {
      return { ...state, step: clamp(Math.round(step), 0, merged.SPEED_STEPS) };
    },

    /**
     * Where the rider's eye is, and which way the train is facing.
     *
     * **Asked of the route rather than integrated**, which is what makes the train unable to drift off the
     * rails. The heading comes straight from the station, so it is the direction the track points rather than
     * the direction the train happens to have been moving.
     *
     * @param {object} state
     * @param {{poseAt: Function}} route - Something that can answer where a distance along the line is.
     * @returns {{x: number, y: number, z: number, heading: number, roll: number, look: number, speed: number}}
     */
    poseFor(state, route) {
      const at = route.poseAt(state.distance);

      /*
        Cant, from the curvature the route reports. Signed, so the train leans into the curve rather than
        away from it, and scaled against the tightest curve the surveyor will lay — so the deepest lean is
        exactly `MAX_CANT` and nothing has to be clamped after the fact.
      */
      const roll = clamp(at.curvature * merged.MIN_RADIUS, -1, 1) * maxCant;

      return {
        x: at.x,
        y: at.height,
        z: at.z,
        heading: at.heading,
        roll,
        look: state.look,
        speed: state.speed,
      };
    },

    /**
     * What to show the player about the throttle.
     *
     * @param {object} state
     * @returns {{step: number, steps: number, targetSpeed: number, stopped: boolean}}
     */
    describeThrottle(state) {
      return {
        step: state.step,
        steps: merged.SPEED_STEPS,
        targetSpeed: speedForStep(state.step),
        stopped: state.step === 0,
      };
    },
  };
}

/**
 * Interpolate between two train states for rendering.
 *
 * The simulation advances in fixed steps and the display does not, so without this a high-refresh monitor
 * shows the simulation's cadence rather than its own. The same reason `blendFlightState` exists.
 *
 * `step` is not blended: it is a setting rather than a motion, and averaging two integers would show a
 * throttle position the player never selected.
 *
 * @param {object} previous
 * @param {object} current
 * @param {number} alpha - 0 at `previous`, 1 at `current`.
 * @returns {object}
 */
export function blendTrainState(previous, current, alpha) {
  const t = clamp(alpha, 0, 1);
  const mix = (from, to) => from + (to - from) * t;

  return {
    distance: mix(previous.distance, current.distance),
    step: current.step,
    speed: mix(previous.speed, current.speed),
    look: mix(previous.look, current.look),
  };
}
